/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { useReportStore } from '@store';
import {
  usePatientIntrospectionQuery,
  usePatientPreviewQuery,
} from '@common/cache';
import { IColumnDefinition } from '@common/types';
import { IPatientsPreviewFilters, IPreviewTableFilters } from '../../types.ts';
import { usePreviewContext } from '../contexts';

type PaginationFilters = Pick<IPatientsPreviewFilters, 'page' | 'limit'>;

const basePaginationFilters: PaginationFilters = {
  page: 1,
  limit: 100,
};

export const usePatientsTable = (group_name?: string) => {
  const { report, setIntrospection } = useReportStore();
  const { data } = usePatientIntrospectionQuery();
  const contextValues = usePreviewContext();
  const { filters, handleFilterChange } = contextValues;

  const [paginationFilters, setPaginationFilters] = useState<PaginationFilters>(
    basePaginationFilters,
  );

  useEffect(() => {
    if (data) {
      setIntrospection(data);
    }
  }, [data]);

  const onFilterChange = (newFilters: Partial<IPreviewTableFilters>) => {
    if (
      newFilters.limit !== paginationFilters.limit ||
      newFilters.page !== paginationFilters.page
    ) {
      setPaginationFilters({ page: newFilters.page, limit: newFilters.limit });
    } else {
      handleFilterChange(newFilters);
      setPaginationFilters({ ...paginationFilters, page: 1 });
    }
  };

  // const location = useLocation();
  // const isPreview = location.pathname.includes('preview');

  const groupByFilter = (() => {
    if (!filters.group_by) {
      return [];
    }

    if (group_name === null) {
      return [
        {
          field: filters.group_by,
          operator: '$isnull',
          value: true,
        },
      ];
    }

    return [
      {
        field: filters.group_by,
        operator: '$eq',
        value: group_name,
      },
    ];
  })();

  const { data: previewData, isFetching: isPreviewFetching } =
    usePatientPreviewQuery({
      ...(filters as any),
      ...paginationFilters,
      options: {
        ...filters.options,
        $and: [...(filters.options?.$and ?? []), ...groupByFilter],
      },
      // enabled: isPreview,
    });

  // const { data: itemsData, isFetching: isItemsFetching } =
  //   usePatientReportItemsQuery(
  //     report?.id ?? '',
  //     {
  //       ...(filters as any),
  //       ...paginationFilters,
  //       $and: [...(filters.options?.$and ?? []), ...groupByFilter],
  //     },
  //     !isPreview,
  //   );

  const entries = previewData?.patients ?? [];
  const totalCount = previewData?.count ?? 0;

  const allPossibleColumns = data?.groups?.flatMap(o => o.fields) ?? [];

  const columns: IColumnDefinition[] =
    report?.options?.select
      .map(rep => {
        const column = allPossibleColumns.find(col => col.field === rep);
        return column;
      })
      .filter(o => o !== undefined)
      .map(o => ({
        id: o.field,
        label: o.label,
        sortable: true,
      })) ?? [];

  return {
    entries,
    columns,
    totalCount,
    isLoading: isPreviewFetching,
    contextValues,
    onFilterChange,
    filters: { ...filters, ...paginationFilters },
  };
};
